import React, {useState, useEffect} from 'react';
import {Box, VStack, Wrap, WrapItem, useToast} from '@chakra-ui/react';
import {TextG12, TextG14} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import ToggleChip from '../../../components/ToggleChip';
import {useCityProfile, useUpsertCityProfile} from '../../../hooks/useCityProfile';
import styled from 'styled-components';

const SaveButton = styled.button<{$disabled?: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#e02f84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

// Backend CityDuration enum values
const CITY_DURATION_OPTIONS = [
  {id: 'less_than_1_year', label: 'Less than 1 year', emoji: '🚀'},
  {id: '1_3_years', label: '1-3 years', emoji: '🌟'},
  {id: '4_7_years', label: '4-7 years', emoji: '🍊'},
  {id: '8_plus_years', label: '8+ years', emoji: '🌴'},
  {id: 'all_my_life', label: 'All my life', emoji: '🎉'},
];

const NEIGHBORHOOD_DURATION_OPTIONS = [
  {id: 'less_than_1_year', label: 'Less than 1 year'},
  {id: '1_3_years', label: '1-3 years'},
  {id: '4_7_years', label: '4-7 years'},
  {id: '8_plus_years', label: '8+ years'},
  {id: 'all_my_life', label: 'All my life'},
];

const HOME_DURATION_OPTIONS = [
  {id: 'less_than_1_year', label: 'Less than 1 year'},
  {id: '1_3_years', label: '1-3 years'},
  {id: '4_7_years', label: '4-7 years'},
  {id: '8_plus_years', label: '8+ years'},
  {id: 'all_my_life', label: 'All my life'},
];

interface FormData {
  city_duration: string | null;
  neighborhood_duration: string | null;
  home_duration: string | null;
  is_renting: boolean | null;
  looking_for_new: boolean | null;
}

const EMPTY_FORM: FormData = {
  city_duration: null,
  neighborhood_duration: null,
  home_duration: null,
  is_renting: null,
  looking_for_new: null,
};

interface EditMyTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  cityName?: string;
}

const EditMyTimeModal = ({isOpen, onClose, cityName = 'San Diego'}: EditMyTimeModalProps) => {
  const {data: cityProfile} = useCityProfile();
  const upsertMutation = useUpsertCityProfile();
  const toast = useToast();
  const [form, setForm] = useState<FormData>(EMPTY_FORM);

  useEffect(() => {
    if (isOpen && cityProfile) {
      setForm({
        city_duration: cityProfile.city_duration,
        neighborhood_duration: cityProfile.neighborhood_duration,
        home_duration: cityProfile.home_duration,
        is_renting: cityProfile.is_renting,
        looking_for_new: cityProfile.looking_for_new,
      });
    }
  }, [isOpen, cityProfile]);

  const toggleDuration = (
    field: 'city_duration' | 'neighborhood_duration' | 'home_duration',
    value: string,
  ) => {
    setForm((prev) => ({...prev, [field]: prev[field] === value ? null : value}));
  };

  const toggleBool = (field: 'is_renting' | 'looking_for_new', value: boolean) => {
    setForm((prev) => ({...prev, [field]: prev[field] === value ? null : value}));
  };

  const handleSave = () => {
    upsertMutation.mutate(form, {
      onSuccess: () => {
        toast({title: 'My Time updated!', status: 'success', duration: 2000});
        onClose();
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.message || 'Could not update My Time';
        toast({title: msg, status: 'error', duration: 3000});
      },
    });
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton
      closeButtonPosition="right"
      scrollBehavior="inside"
      header={
        <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
          My Time in {cityName}
        </TextG14>
      }
      modalPadding="16px 20px 24px">
      <VStack spacing={5} align="stretch">
        {/* Info box */}
        <Box
          bg="#F9F9F9"
          borderRadius="14px"
          p={4}
          border="2px solid #E9E9E9"
          boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08);">
          <TextG14 mt="0" color="#1A1A1A" lineHeight="20px">
            Some people are new. Some know everyone. This helps us understand where you're at in
            your neighborhood.
          </TextG14>
        </Box>

        {/* Q1: How long in city */}
        <Box>
          <TextG14 mt="0" fontWeight="700" mb={3}>
            How long have you been in {cityName}?
          </TextG14>
          <Wrap spacing={2}>
            {CITY_DURATION_OPTIONS.map((opt) => (
              <WrapItem key={opt.id}>
                <ToggleChip
                  label={opt.label}
                  emoji={opt.emoji}
                  isSelected={form.city_duration === opt.id}
                  onToggle={() => toggleDuration('city_duration', opt.id)}
                />
              </WrapItem>
            ))}
          </Wrap>
        </Box>

        {/* Q2: How long in neighborhood */}
        <Box>
          <TextG14 mt="0" fontWeight="700" mb={3}>
            How long have you been in your current neighborhood?
          </TextG14>
          <Wrap spacing={2}>
            {NEIGHBORHOOD_DURATION_OPTIONS.map((opt) => (
              <WrapItem key={opt.id}>
                <ToggleChip
                  label={opt.label}
                  isSelected={form.neighborhood_duration === opt.id}
                  onToggle={() => toggleDuration('neighborhood_duration', opt.id)}
                />
              </WrapItem>
            ))}
          </Wrap>
        </Box>

        {/* Q3: How long in home */}
        <Box>
          <TextG14 mt="0" fontWeight="700" mb={3}>
            How long have you been in your current home?{' '}
            <TextG12 as="span" mt="0" color="#757D84">
              (So we can alert the welcome wagon)
            </TextG12>
          </TextG14>
          <Wrap spacing={2}>
            {HOME_DURATION_OPTIONS.map((opt) => (
              <WrapItem key={opt.id}>
                <ToggleChip
                  label={opt.label}
                  isSelected={form.home_duration === opt.id}
                  onToggle={() => toggleDuration('home_duration', opt.id)}
                />
              </WrapItem>
            ))}
          </Wrap>
        </Box>

        {/* Q4: Rent or own */}
        <Box>
          <TextG14 mt="0" fontWeight="700" mb={1}>
            Do you rent or own your home?
          </TextG14>
          <TextG12 mt="0" color="#757D84" mb={3}>
            So we can support you in feeling at home where you live.
          </TextG12>
          <Wrap spacing={2}>
            <WrapItem>
              <ToggleChip
                label="Rent"
                isSelected={form.is_renting === true}
                onToggle={() => toggleBool('is_renting', true)}
              />
            </WrapItem>
            <WrapItem>
              <ToggleChip
                label="Own"
                isSelected={form.is_renting === false}
                onToggle={() => toggleBool('is_renting', false)}
              />
            </WrapItem>
          </Wrap>
        </Box>

        {/* Q5: Looking for new place */}
        <Box>
          <TextG14 mt="0" fontWeight="700" mb={1}>
            Are you currently looking for a new place?
          </TextG14>
          <TextG12 mt="0" color="#757D84" mb={3}>
            Walkabout sometimes shares local apartments before they're listed publicly. Insiders may
            also get waived application fees.
          </TextG12>
          <Wrap spacing={2}>
            <WrapItem>
              <ToggleChip
                label="Yes"
                isSelected={form.looking_for_new === true}
                onToggle={() => toggleBool('looking_for_new', true)}
              />
            </WrapItem>
            <WrapItem>
              <ToggleChip
                label="No"
                isSelected={form.looking_for_new === false}
                onToggle={() => toggleBool('looking_for_new', false)}
              />
            </WrapItem>
          </Wrap>
        </Box>

        <SaveButton $disabled={upsertMutation.isLoading} onClick={handleSave}>
          {upsertMutation.isLoading ? 'Saving...' : 'Save'}
        </SaveButton>
      </VStack>
    </Modal>
  );
};

export default EditMyTimeModal;
